import { useMemo } from 'react'
import type {
  Requirement,
  RequirementMetadata,
  RequirementTemplate,
  RequirementType,
} from '@ispot/shared'
import { useRequirementTemplates } from '@/hooks/useRequirementTemplates'
import { SectionDispatcher } from './SectionDispatcher'
import type { NotesMode } from './types'

interface Props {
  accountId: string
  projectId: string
  requirement: Requirement
  /** Update arbitrary requirement fields (e.g. description). */
  onUpdateRequirement: (id: string, patch: Partial<Requirement>) => Promise<unknown>
}

const MODE: NotesMode = 'clean'

export function NotesEditor({ accountId, projectId, requirement, onUpdateRequirement }: Props) {
  const { templates, isLoading } = useRequirementTemplates(accountId, projectId)

  const template = useMemo(() => resolveTemplate(templates, requirement.templateId, requirement.type), [
    templates, requirement.templateId, requirement.type,
  ])

  const updateMeta = (patch: Partial<RequirementMetadata>) => {
    onUpdateRequirement(requirement.id, {
      metadata: { ...requirement.metadata, ...patch },
    })
  }

  const updateRequirement = (patch: Partial<Requirement>) => {
    onUpdateRequirement(requirement.id, patch)
  }

  if (isLoading && !template) {
    return (
      <div style={{ padding: 24, color: 'var(--color-fg-muted)', fontSize: 13 }}>
        Loading template…
      </div>
    )
  }

  if (!template) {
    return (
      <div style={{ padding: '20px 24px', color: 'var(--color-fg-muted)', fontSize: 13 }}>
        No template configured for this requirement type.
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{
          padding: '8px 28px 64px',
          background: 'var(--color-warm-50)',
        }}
      >
        {template.sections.map((section) => (
          <SectionDispatcher
            key={section.id}
            section={section}
            requirement={requirement}
            mode={MODE}
            onUpdateRequirement={updateRequirement}
            onUpdateMetadata={updateMeta}
          />
        ))}
      </div>
    </div>
  )
}

function resolveTemplate(
  templates: RequirementTemplate[],
  explicitId: string | null,
  type: RequirementType,
): RequirementTemplate | null {
  if (explicitId) {
    const exact = templates.find(t => t.id === explicitId)
    if (exact) return exact
  }
  return (
    templates.find(t => t.appliesTo === type && t.isDefault)
    ?? templates.find(t => t.appliesTo === type)
    ?? null
  )
}
